// Where each widget sits on the dashboard: a box on a 12-column grid, dragged and resized freely
// (react-grid-layout). Replaced the older layout of rows split by percentage widths, which forced
// everything in a row to one height — a row of single figures next to a table was as tall as the
// table. Pure, so the conversion from rows is tested without a DOM.

export const DASHBOARD_GRID_COLS = 12;
// One height unit, and the gap between boxes, in px. A box `h` units tall is
// h * ROW_HEIGHT + (h - 1) * GAP pixels.
export const DASHBOARD_GRID_ROW_HEIGHT = 30;
export const DASHBOARD_GRID_GAP = 16;
export const DASHBOARD_GRID_MIN_W = 2;
export const DASHBOARD_GRID_MIN_H = 3;

export type DashboardGridItem = { widgetId: string; x: number; y: number; w: number; h: number };

// The layout before version 3: rows of widgets, each a percentage of the row's width.
export type LegacyLayoutRow = { items: { widgetId: string; widthPct: number }[] };

type Size = { w: number; h: number };

// Where a new widget starts: a single figure small, a table wide and tall, a chart in between.
export const defaultWidgetGridSize = (graphType?: string): Size => {
    if (graphType === 'stat') return { w: 4, h: 4 };
    if (graphType === 'table') return { w: 12, h: 11 };
    return { w: 6, h: 9 };
};

// The height the row layout gave every widget (a 300px body, its heading and the breakdown
// link), except a single figure, which never needed it.
const legacyHeight = (graphType?: string): number => (graphType === 'stat' ? 4 : 9);

export const gridItemHeightPx = (h: number): number =>
    h * DASHBOARD_GRID_ROW_HEIGHT + Math.max(0, h - 1) * DASHBOARD_GRID_GAP;

const int = (value: unknown, fallback: number): number =>
    Number.isFinite(value) ? Math.round(value as number) : fallback;

// The old rows' own clean-up, kept so a stored row converts exactly as it used to display:
// each item at least 15%, the row scaled to add up to 100.
const LEGACY_MIN_PCT = 15;
const legacyRowWidths = (pcts: number[]): number[] => {
    const clamped = pcts.map(p => Math.max(LEGACY_MIN_PCT, Number.isFinite(p) ? p : 0));
    if (clamped.length > Math.floor(100 / LEGACY_MIN_PCT)) return clamped;
    const total = clamped.reduce((a, b) => a + b, 0);
    return clamped.map(p => (p / total) * 100);
};

// Rows → boxes. Each row becomes a band as tall as its tallest widget, the widths kept as near
// as twelve columns allow; a row too crowded to fit wraps onto a second band.
export const rowsToGrid = (
    rows: LegacyLayoutRow[],
    graphTypeOf: (widgetId: string) => string | undefined,
): DashboardGridItem[] => {
    const items: DashboardGridItem[] = [];
    let y = 0;
    // Everything in a row was one height, so each band takes its tallest widget's.
    let band: number[] = [];
    const closeBand = () => {
        const bandH = band.reduce((max, i) => Math.max(max, items[i].h), 0);
        band.forEach(i => {
            items[i] = { ...items[i], h: bandH };
        });
        y += bandH;
        band = [];
    };
    rows.forEach(row => {
        const widths = legacyRowWidths(row.items.map(it => it.widthPct));
        let x = 0;
        let cum = 0;
        row.items.forEach((it, i) => {
            const start = Math.round((cum / 100) * DASHBOARD_GRID_COLS);
            cum += widths[i];
            const w = Math.max(
                DASHBOARD_GRID_MIN_W,
                Math.round((cum / 100) * DASHBOARD_GRID_COLS) - start,
            );
            if (x + w > DASHBOARD_GRID_COLS) {
                closeBand();
                x = 0;
            }
            band.push(items.length);
            items.push({
                widgetId: it.widgetId,
                x,
                y,
                w,
                h: legacyHeight(graphTypeOf(it.widgetId)),
            });
            x += w;
        });
        closeBand();
    });
    return items;
};

// Keep the boxes in step with the widgets: drop boxes for widgets that are gone (and repeats),
// pull anything off the edge back onto the grid, and give each widget without a box one of its
// default size below everything else.
export const normalizeGrid = (
    items: DashboardGridItem[],
    widgets: { id: string; graphType?: string }[],
): DashboardGridItem[] => {
    const ids = new Set(widgets.map(w => w.id));
    const seen = new Set<string>();
    const placed: DashboardGridItem[] = [];
    items.forEach(it => {
        if (!it || !ids.has(it.widgetId) || seen.has(it.widgetId)) return;
        seen.add(it.widgetId);
        const w = Math.min(
            DASHBOARD_GRID_COLS,
            Math.max(DASHBOARD_GRID_MIN_W, int(it.w, DASHBOARD_GRID_COLS)),
        );
        placed.push({
            widgetId: it.widgetId,
            x: Math.min(DASHBOARD_GRID_COLS - w, Math.max(0, int(it.x, 0))),
            y: Math.max(0, int(it.y, 0)),
            w,
            h: Math.max(DASHBOARD_GRID_MIN_H, int(it.h, 9)),
        });
    });
    let y = placed.reduce((max, it) => Math.max(max, it.y + it.h), 0);
    let x = 0;
    let bandH = 0;
    widgets
        .filter(w => !seen.has(w.id))
        .forEach(widget => {
            const size = defaultWidgetGridSize(widget.graphType);
            if (x + size.w > DASHBOARD_GRID_COLS) {
                y += bandH;
                x = 0;
                bandH = 0;
            }
            placed.push({ widgetId: widget.id, x, y, ...size });
            x += size.w;
            bandH = Math.max(bandH, size.h);
        });
    return placed;
};

// Reading order (top to bottom, then left to right): how the boxes stack on a narrow screen,
// and the order the "all data" download lists them in.
export const gridReadingOrder = (items: DashboardGridItem[]): DashboardGridItem[] =>
    [...items].sort((a, b) => a.y - b.y || a.x - b.x);
